import { Product, PricingItem } from '../services/admin.service';

/**
 * Resuelve qué consulta de pricing hace el frontend de clientes para un
 * producto. Los productos NO se cruzan por su typeFamily (un marketplace app
 * tiene typeFamily EC2 pero cobra con planes APP_DEPLOY): cada flujo del
 * cliente pide una familia fija. Mapeo verificado contra kubiy-cloud-ui:
 *
 *   Deploy Apps  -> getPlans('APP_DEPLOY', 'GENERIC', 'SERVER'|'STATIC')
 *   Marketplace  -> getPlans('APP_DEPLOY', 'GENERIC', 'SERVER')
 *   VMs          -> getPlans('EC2', product.osType, product.osFamily)
 *   Databases    -> getPlans('RDS', engine.toUpperCase())
 *   Chatbot IA   -> getPlans('AI', 'GENERIC')
 *
 * El producto puede sobreescribir el mapeo con pricingTypeFamily /
 * pricingOsType / pricingOsFamily (campos opcionales editables en el admin).
 */

export interface PricingRef {
  typeFamily: string;
  osType: string | null;
  osFamily: string | null;
}

const norm = (s: string): string => s.toUpperCase().replace(/-/g, '_');

export const resolvePricingRef = (product: Product): PricingRef => {
  const anyProduct = product as any;
  if (anyProduct.pricingTypeFamily) {
    return {
      typeFamily: norm(String(anyProduct.pricingTypeFamily)),
      osType: anyProduct.pricingOsType ? norm(String(anyProduct.pricingOsType)) : null,
      osFamily: anyProduct.pricingOsFamily ? norm(String(anyProduct.pricingOsFamily)) : null,
    };
  }
  switch ((product.category || '').toUpperCase()) {
    case 'VM':
      return {
        typeFamily: 'EC2',
        osType: product.osType ? norm(product.osType) : null,
        osFamily: product.osFamily ? norm(product.osFamily) : null,
      };
    case 'DATABASE':
      return {
        typeFamily: 'RDS',
        osType: product.engine ? norm(product.engine) : null,
        osFamily: null,
      };
    case 'SITE':
      return { typeFamily: 'APP_DEPLOY', osType: 'GENERIC', osFamily: 'STATIC' };
    case 'APP_DEPLOY':
      return { typeFamily: 'APP_DEPLOY', osType: 'GENERIC', osFamily: 'SERVER' };
    case 'AI':
    case 'AI_SERVICE':
    case 'CHATBOT':
      return { typeFamily: 'AI', osType: 'GENERIC', osFamily: null };
    default:
      // Apps del marketplace (GHOST, ODOO, N8N, ...) usan el selector de
      // pricing del marketplace: APP_DEPLOY GENERIC SERVER
      return { typeFamily: 'APP_DEPLOY', osType: 'GENERIC', osFamily: 'SERVER' };
  }
};

const itemOsFamily = (item: PricingItem): string | null => {
  const anyItem = item as any;
  if (anyItem.osFamily) return norm(String(anyItem.osFamily));
  const match = item.SK.match(/#FAMILY#([^#]+)/);
  return match ? norm(match[1]) : null;
};

/** ¿Este plan aparecería en la consulta que hace el cliente para este ref? */
export const planMatchesRef = (plan: PricingItem, ref: PricingRef): boolean => {
  if (norm(plan.typeFamily || '') !== ref.typeFamily) return false;
  if (ref.osType && plan.osType && norm(plan.osType) !== ref.osType) return false;
  if (ref.osFamily) {
    const family = itemOsFamily(plan);
    // el backend filtra con begins_with que incluye FAMILY#<osFamily>:
    // un plan sin ese segmento no aparece en la consulta del cliente
    if (family !== ref.osFamily) return false;
  }
  return true;
};

/** Add-ons: se muestran por familia completa (el osType de los add-ons no
 *  siempre coincide con el de los planes, ej. APP_DEPLOY GENERIC vs LINUX). */
export const addonMatchesRef = (addon: PricingItem, ref: PricingRef): boolean =>
  norm(addon.typeFamily || '') === ref.typeFamily;

export const formatRef = (ref: PricingRef): string =>
  [ref.typeFamily, ref.osType, ref.osFamily].filter(Boolean).join(' · ');
